import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Role, Member } from "@chat-yai/shared";
import type {
  ConversationListItem,
  ConversationStatusWritable,
  ConversationSummaryStatus,
} from "@chat-yai/shared/admin-types";
import { conversationsApi } from "@/api/endpoints/conversations";
import { tenantsApi } from "@/api/endpoints/tenants";
import { Drawer } from "@/components/Drawer";
import { Icon } from "@/components/Icon";
import { renderInlineMarkdown } from "@/lib/markdown";
import { EmptyState } from "@/components/EmptyState";
import { canManageContent } from "@/lib/rbac";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";

type StatusFilter = "all" | ConversationStatusWritable;
type ChannelFilter = "all" | "chat" | "web_voice" | "phone";

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "pending", label: "Pending" },
  { key: "closed", label: "Closed" },
  { key: "archived", label: "Archived" },
];

const CHANNEL_FILTERS: { key: ChannelFilter; label: string }[] = [
  { key: "all", label: "All channels" },
  { key: "chat", label: "Chat" },
  { key: "web_voice", label: "Web voice" },
  { key: "phone", label: "Phone" },
];

const WRITABLE_STATUSES: ConversationStatusWritable[] = [
  "open",
  "pending",
  "closed",
  "archived",
];

interface Props {
  // chatbotId is omitted on the global /conversations page so the list
  // shows every chatbot in the workspace.
  chatbotId?: string;
  role: Role;
}

export function ChatbotConversationsTab({ chatbotId, role }: Props): JSX.Element {
  return <ConversationsList chatbotId={chatbotId} role={role} />;
}

export function ConversationsList({ chatbotId, role }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const canEdit = canManageContent(role);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [openItem, setOpenItem] = useState<ConversationListItem | null>(null);

  const membersQuery = useQuery({
    queryKey: ["tenants", "current", "members"],
    queryFn: () => tenantsApi.members(),
  });
  const members: Member[] = membersQuery.data?.members ?? [];
  const assignableMembers = members.filter(
    (m) => m.role !== "viewer", // viewer gets read-only conversations only
  );

  const listQuery = useQuery({
    queryKey: [
      "conversations",
      chatbotId ?? "all",
      { page, q: search, statusFilter, channelFilter, assigneeFilter, unreadOnly },
    ],
    queryFn: () =>
      conversationsApi.list({
        ...(chatbotId ? { chatbot_id: chatbotId } : {}),
        ...(channelFilter !== "all" ? { channel: channelFilter } : {}),
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
        ...(assigneeFilter !== "all"
          ? { assignee_user_id: assigneeFilter === "unassigned" ? null : assigneeFilter }
          : {}),
        ...(unreadOnly ? { unread_only: true } : {}),
        ...(statusFilter === "archived" ? { include_archived: true } : {}),
        page,
        page_size: 20,
        q: search || undefined,
      }),
  });

  const detailQuery = useQuery({
    queryKey: ["conversations", "detail", openItem?.id],
    queryFn: () => conversationsApi.get(openItem!.id),
    enabled: !!openItem,
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: Parameters<typeof conversationsApi.update>[1];
    }) => conversationsApi.update(id, input),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      // Sync the open drawer's local copy so the chips update without a
      // full re-fetch flicker.
      if (openItem && res.conversation.id === openItem.id) {
        setOpenItem(res.conversation);
      }
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const summarizeMutation = useMutation({
    mutationFn: (id: string) => conversationsApi.summarize(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      toast.push("success", "Summary requested.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));
  const items = listQuery.data?.items ?? [];

  const channelLabel = (channel: ConversationListItem["channel"]): string => {
    if (channel === "web_voice") return "Voice";
    if (channel === "phone") return "Phone";
    return "Chat";
  };
  const channelCell = (
    channel: ConversationListItem["channel"],
  ): JSX.Element => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 12,
        color: "var(--text-3)",
      }}
      data-testid={`conversation-channel-${channel}`}
    >
      {channel === "web_voice" ? (
        <svg
          viewBox="0 0 24 24"
          width={12}
          height={12}
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 14a3 3 0 0 0 3-3V7a3 3 0 0 0-6 0v4a3 3 0 0 0 3 3Z"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <path
            d="M5 11a7 7 0 0 0 14 0M12 18v3"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      ) : null}
      {channelLabel(channel)}
    </span>
  );

  return (
    <div style={{ marginTop: 16 }}>
      <div className="filters-bar" style={{ flexWrap: "wrap", gap: 8 }}>
        <div className="search-input">
          <Icon name="search" size={14} />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search messages"
            data-testid="conversations-search"
          />
        </div>
        <div
          role="tablist"
          aria-label="Filter by status"
          style={{ display: "flex", gap: 4, flexWrap: "wrap" }}
          data-testid="conversations-status-filters"
        >
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={statusFilter === f.key}
              className={"chip " + (statusFilter === f.key ? "chip-accent" : "")}
              style={{ cursor: "pointer", border: 0, padding: "4px 10px" }}
              onClick={() => {
                setStatusFilter(f.key);
                setPage(1);
              }}
              data-testid={`conversations-status-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div
          role="tablist"
          aria-label="Filter by channel"
          style={{ display: "flex", gap: 4, flexWrap: "wrap" }}
          data-testid="conversations-channel-filters"
        >
          {CHANNEL_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={channelFilter === f.key}
              className={"chip " + (channelFilter === f.key ? "chip-accent" : "")}
              style={{ cursor: "pointer", border: 0, padding: "4px 10px" }}
              onClick={() => {
                setChannelFilter(f.key);
                setPage(1);
              }}
              data-testid={`conversations-channel-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select
          className="field-input"
          style={{ width: 180 }}
          value={assigneeFilter}
          onChange={(e) => {
            setAssigneeFilter(e.target.value);
            setPage(1);
          }}
          data-testid="conversations-assignee-filter"
        >
          <option value="all">All assignees</option>
          <option value="unassigned">Unassigned</option>
          {members.map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {m.email}
            </option>
          ))}
        </select>
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: "var(--text-2)",
          }}
        >
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => {
              setUnreadOnly(e.target.checked);
              setPage(1);
            }}
            data-testid="conversations-unread-only"
          />
          Unread only
        </label>
      </div>

      <div className="card">
        <div
          className="table-row-head"
          style={{ gridTemplateColumns: "24px 1.4fr 2fr 1fr 90px 130px 100px" }}
        >
          <div></div>
          <div>Session</div>
          <div>Last message</div>
          <div>Assignee</div>
          <div>Channel</div>
          <div>Last activity</div>
          <div>Status</div>
        </div>
        {items.map((c) => {
          const isUnread = !!c.unread;
          return (
            <button
              key={c.id}
              type="button"
              className="list-row"
              onClick={() => setOpenItem(c)}
              style={{
                gridTemplateColumns: "24px 1.4fr 2fr 1fr 90px 130px 100px",
                border: 0,
                background: isUnread ? "var(--surface-2, transparent)" : "transparent",
                textAlign: "left",
                cursor: "pointer",
                font: "inherit",
                color: "inherit",
              }}
              data-testid={`conversation-row-${c.id}`}
              data-unread={isUnread ? "true" : "false"}
            >
              <div aria-hidden="true">
                {isUnread ? (
                  <span
                    title="Unread"
                    aria-label="Unread"
                    data-testid={`conversation-unread-dot-${c.id}`}
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "var(--accent, oklch(0.6 0.18 240))",
                    }}
                  />
                ) : null}
              </div>
              <div>
                <div
                  className="mono"
                  style={{ fontSize: 11, color: "var(--text-3)" }}
                >
                  {c.session_id.slice(0, 18)}
                  {c.session_id.length > 18 ? "…" : ""}
                </div>
                {c.source_url ? (
                  <div
                    className="mono"
                    style={{ fontSize: 10, color: "var(--text-3)" }}
                    title={c.source_url}
                  >
                    {sourceShort(c.source_url)}
                  </div>
                ) : null}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--text-2)",
                  fontWeight: isUnread ? 600 : 400,
                }}
              >
                {c.preview || "—"}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-2)" }}>
                {c.assignee_email ?? "—"}
              </div>
              <div>{channelCell(c.channel)}</div>
              <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                {new Date(c.last_message_at).toLocaleString()}
              </div>
              <div>
                <span className={"chip " + statusChipClass(c.status)}>
                  {c.status}
                </span>
              </div>
            </button>
          );
        })}
        {items.length === 0 ? (
          search || statusFilter !== "all" || assigneeFilter !== "all" || unreadOnly ? (
            <div className="empty">No conversations match those filters.</div>
          ) : (
            <EmptyState
              icon="bot"
              title="No conversations yet"
              description="Once you embed the widget on your site, every visitor session lands here so you can review transcripts and quality-check the assistant."
              testId="empty-conversations"
            />
          )
        ) : null}
        {total > 20 ? (
          <div className="pagination">
            <span>
              Page {page} of {totalPages} · {total} conversations
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                className="btn btn-outline btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                className="btn btn-outline btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <Drawer
        title={openItem ? `Session ${openItem.session_id.slice(0, 12)}…` : "Conversation"}
        open={!!openItem}
        onClose={() => setOpenItem(null)}
        width={600}
      >
        {openItem ? (
          <ConversationDetailPanel
            item={openItem}
            canEdit={canEdit}
            members={assignableMembers}
            detail={detailQuery.data}
            isLoading={detailQuery.isLoading}
            onMarkRead={() =>
              updateMutation.mutate({
                id: openItem.id,
                input: { unread: false },
              })
            }
            onStatusChange={(status) =>
              updateMutation.mutate({
                id: openItem.id,
                input: { status },
              })
            }
            onAssign={(userId) =>
              updateMutation.mutate({
                id: openItem.id,
                input: { assignee_user_id: userId },
              })
            }
            onResummarize={() => summarizeMutation.mutate(openItem.id)}
          />
        ) : null}
      </Drawer>
    </div>
  );
}

function statusChipClass(s: ConversationListItem["status"]): string {
  if (s === "closed") return "chip-warning";
  if (s === "archived") return "";
  if (s === "pending") return "chip-accent";
  // open / active
  return "chip-success";
}

function sourceShort(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname.length > 24 ? u.pathname.slice(0, 24) + "…" : u.pathname;
    return u.host + path;
  } catch {
    return url.slice(0, 32);
  }
}

interface ConversationDetailPanelProps {
  item: ConversationListItem;
  canEdit: boolean;
  members: Member[];
  detail:
    | {
        conversation: ConversationListItem & {
          client_metadata: Record<string, unknown> | null;
        };
        messages: {
          id: string;
          role: "user" | "assistant" | "system" | "tool";
          content: string;
          created_at: string;
          audio_metadata_json?: {
            duration_ms?: number;
            asr_provider?: string;
            tts_provider?: string;
            tts_voice?: string;
            interrupted_at_ms?: number;
          } | null;
        }[];
      }
    | undefined;
  isLoading: boolean;
  onMarkRead: () => void;
  onStatusChange: (status: ConversationStatusWritable) => void;
  onAssign: (userId: string | null) => void;
  onResummarize: () => void;
}

function ConversationDetailPanel({
  item,
  canEdit,
  members,
  detail,
  isLoading,
  onMarkRead,
  onStatusChange,
  onAssign,
  onResummarize,
}: ConversationDetailPanelProps): JSX.Element {
  const summaryStatus: ConversationSummaryStatus = item.summary_status ?? "skipped";
  const maskedIp = useMemo(() => maskIp(item.client_ip ?? null), [item.client_ip]);
  const isVoice = item.channel === "web_voice";
  const isPhone = item.channel === "phone";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {isVoice ? <VoiceHeader item={item} /> : null}
      {isPhone ? <PhoneHeader item={item} /> : null}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
        }}
        data-testid="conversation-detail-controls"
      >
        {item.unread ? (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={onMarkRead}
            disabled={!canEdit}
            data-testid="conversation-mark-read"
          >
            <Icon name="check" size={12} /> Mark as read
          </button>
        ) : (
          <span
            className="chip chip-success"
            data-testid="conversation-read-chip"
          >
            Read
          </span>
        )}
        <select
          className="field-input"
          style={{ height: 28, padding: "2px 8px", fontSize: 12 }}
          value={
            item.status === "active"
              ? "open"
              : (item.status as ConversationStatusWritable)
          }
          disabled={!canEdit}
          onChange={(e) => onStatusChange(e.target.value as ConversationStatusWritable)}
          data-testid="conversation-status-select"
        >
          {WRITABLE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          className="field-input"
          style={{ height: 28, padding: "2px 8px", fontSize: 12, minWidth: 160 }}
          value={item.assignee_user_id ?? ""}
          disabled={!canEdit}
          onChange={(e) => onAssign(e.target.value === "" ? null : e.target.value)}
          data-testid="conversation-assignee-select"
        >
          <option value="">Unassigned</option>
          {members.map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {m.email}
            </option>
          ))}
        </select>
      </div>

      <SummaryPanel
        status={summaryStatus}
        summary={item.summary ?? null}
        canEdit={canEdit}
        onRetry={onResummarize}
      />

      <div
        className="help"
        style={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {item.source_url ? (
          <div>
            Source page:{" "}
            <a href={item.source_url} target="_blank" rel="noreferrer">
              {item.source_url}
            </a>
          </div>
        ) : (
          <div>Source page: Direct</div>
        )}
        {item.source_referrer ? <div>Referrer: {item.source_referrer}</div> : null}
        {item.client_user_agent ? (
          <div>User agent: {item.client_user_agent}</div>
        ) : null}
        {maskedIp ? <div>Visitor IP: {maskedIp}</div> : null}
      </div>

      {isLoading ? (
        <div className="empty">Loading transcript…</div>
      ) : detail ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="help">
            Started {new Date(detail.conversation.started_at).toLocaleString()} ·{" "}
            {detail.messages.length} messages
          </div>
          {detail.messages.map((m) => (
            <MessageBubble
              key={m.id}
              role={m.role}
              content={m.content}
              createdAt={m.created_at}
              audio={m.audio_metadata_json ?? null}
            />
          ))}
        </div>
      ) : (
        <div className="empty">Could not load transcript.</div>
      )}
    </div>
  );
}

function SummaryPanel({
  status,
  summary,
  canEdit,
  onRetry,
}: {
  status: ConversationSummaryStatus;
  summary: string | null;
  canEdit: boolean;
  onRetry: () => void;
}): JSX.Element | null {
  if (status === "skipped") return null;
  let bg = "var(--surface-2)";
  let label = "Summary";
  let body: JSX.Element;
  if (status === "ready" && summary) {
    body = <p style={{ margin: 0, fontSize: 13 }}>{summary}</p>;
  } else if (status === "pending" || status === "in_progress") {
    bg = "var(--surface-2)";
    label = "Summary in progress";
    body = (
      <p style={{ margin: 0, fontSize: 12, color: "var(--text-3)" }}>
        We&apos;ll generate a 3-sentence summary once the conversation closes
        or stays idle. This usually takes under a minute.
      </p>
    );
  } else if (status === "failed") {
    bg = "oklch(0.95 0.05 25)";
    label = "Summary failed";
    body = (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <p style={{ margin: 0, fontSize: 12 }}>
          We couldn&apos;t generate a summary for this conversation.
        </p>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={onRetry}
          disabled={!canEdit}
          data-testid="conversation-summary-retry"
        >
          Retry summary
        </button>
      </div>
    );
  } else {
    return null;
  }
  return (
    <section
      style={{
        background: bg,
        padding: 10,
        borderRadius: 8,
        border: "1px solid var(--border-1)",
      }}
      data-testid={`conversation-summary-${status}`}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          color: "var(--text-3)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {body}
    </section>
  );
}

function maskIp(ip: string | null): string | null {
  if (!ip) return null;
  // IPv4: keep first three octets (/24), mask last.
  if (/^\d+\.\d+\.\d+\.\d+$/.test(ip)) {
    const parts = ip.split(".");
    return `${parts[0]}.${parts[1]}.${parts[2]}.x`;
  }
  // IPv6: keep first 4 groups.
  if (ip.includes(":")) {
    const groups = ip.split(":").slice(0, 4);
    return groups.join(":") + "::xxxx";
  }
  return ip;
}

function MessageBubble({
  role,
  content,
  createdAt,
  audio,
}: {
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  createdAt: string;
  audio?: {
    duration_ms?: number;
    asr_provider?: string;
    tts_provider?: string;
    tts_voice?: string;
    interrupted_at_ms?: number;
  } | null;
}): JSX.Element {
  const isUser = role === "user";
  const isAssistant = role === "assistant";
  const bg = isUser
    ? "oklch(0.96 0.04 240)"
    : isAssistant
      ? "oklch(0.97 0.02 150)"
      : "oklch(0.94 0 0)";
  const html = renderInlineMarkdown(content);
  return (
    <div
      style={{
        background: bg,
        padding: "10px 12px",
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.45,
        border: "1px solid var(--border-1)",
        alignSelf: isUser ? "flex-end" : "flex-start",
        maxWidth: "92%",
      }}
      data-testid={`message-${role}`}
    >
      <div
        style={{
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "var(--text-3)",
          marginBottom: 4,
        }}
      >
        {role} · <span className="mono">{new Date(createdAt).toLocaleTimeString()}</span>
        {audio?.duration_ms ? (
          <>
            {" "}
            ·{" "}
            <span
              className="chip"
              style={{ marginLeft: 4, padding: "1px 6px", fontSize: 10 }}
              data-testid={`message-audio-${role}`}
            >
              {formatDurationMs(audio.duration_ms)}
            </span>
          </>
        ) : null}
        {audio?.interrupted_at_ms ? (
          <>
            {" "}
            ·{" "}
            <span
              className="chip chip-warning"
              style={{ marginLeft: 4, padding: "1px 6px", fontSize: 10 }}
            >
              interrupted
            </span>
          </>
        ) : null}
      </div>
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

function VoiceHeader({ item }: { item: ConversationListItem }): JSX.Element {
  const meta = item.voice_session_meta ?? null;
  const totalSec = meta?.total_seconds ?? 0;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return (
    <section
      data-testid="conversation-voice-header"
      style={{
        background: "oklch(0.96 0.04 240)",
        border: "1px solid var(--border-1)",
        borderRadius: 8,
        padding: 10,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div style={{ fontSize: 11, color: "var(--text-3)", letterSpacing: 0.4 }}>
        WEB VOICE SESSION
      </div>
      <div style={{ fontSize: 14, fontWeight: 600 }}>
        {totalSec > 0 ? `${minutes}m ${seconds}s on the call` : "Voice call"}
      </div>
      {meta ? (
        <div style={{ fontSize: 11, color: "var(--text-3)" }}>
          {meta.lang ?? ""}
          {meta.voice ? ` · voice: ${meta.voice}` : null}
          {meta.hangup_reason ? ` · ${meta.hangup_reason}` : null}
        </div>
      ) : (
        <div style={{ fontSize: 11, color: "var(--text-3)" }}>
          Transcript only — audio is not stored.
        </div>
      )}
    </section>
  );
}

function PhoneHeader({ item }: { item: ConversationListItem }): JSX.Element {
  const meta = item.phone_session_meta ?? null;
  const totalSec = meta?.total_seconds ?? 0;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return (
    <section
      data-testid="conversation-phone-header"
      style={{
        background: "oklch(0.95 0.05 80)",
        border: "1px solid var(--border-1)",
        borderRadius: 8,
        padding: 10,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div style={{ fontSize: 11, color: "var(--text-3)", letterSpacing: 0.4 }}>
        PHONE CALL
      </div>
      <div style={{ fontSize: 14, fontWeight: 600 }} data-testid="conversation-phone-duration">
        {totalSec > 0 ? `${minutes}m ${seconds}s call` : "Phone call"}
      </div>
      {meta ? (
        <div style={{ fontSize: 12, color: "var(--text-2)" }}>
          <div data-testid="conversation-phone-caller">
            From <span className="mono">{meta.from}</span>
            {meta.to ? (
              <>
                {" "}to <span className="mono">{meta.to}</span>
              </>
            ) : null}
          </div>
          <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
            Call SID: {meta.call_sid}
          </div>
          {meta.hangup_reason ? (
            <div style={{ fontSize: 11, color: "var(--text-3)" }}>
              Ended: {meta.hangup_reason}
            </div>
          ) : null}
        </div>
      ) : (
        <div style={{ fontSize: 11, color: "var(--text-3)" }}>
          Transcript only — call audio is not stored.
        </div>
      )}
    </section>
  );
}

function formatDurationMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = Math.floor(seconds / 60);
  const rem = Math.round(seconds - minutes * 60);
  return `${minutes}m ${rem}s`;
}
